import React, { useState, useContext, useEffect } from 'react';
import { useTranslation, Trans } from 'react-i18next';

import {
  FormControl, FormLabel, Input, Button, Text, Box, InputGroup, IconButton,
  InputRightElement, Flex, Alert, AlertIcon, AlertTitle, AlertDescription,
  Spinner,
} from '@chakra-ui/react'; // Import necessary Chakra UI components
import fetchData from './utils/Util'; // Import fetchData utility function
import PhoneInput, { getCountries } from 'react-phone-number-input';
import flags from 'react-phone-number-input/flags'; // Import the flags module
import { formatPhoneNumberIntl } from 'react-phone-number-input';
import ReCAPTCHA from 'react-google-recaptcha';
import { MdVisibility, MdVisibilityOff, MdCheckCircle, MdError } from 'react-icons/md'; // Importing eye, check circle, and error icons
import { ConfigContext } from './utils/Config';
import MyAccount from './MyAccount';

const Register = () => {
  const { t, i18n } = useTranslation();
  const config = useContext(ConfigContext);
  // State for form data
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    mail: '',
    pass: '',
    mobile: '',
    language: i18n.language,
    recaptcha_token: '',
    site: config.siteName,
  });

  // State for phone input
  const [country, setCountry] = useState('US'); // Define country state
  const [formattedMobile, setFormattedMobile] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isConfirmPasswordTouched, setIsConfirmPasswordTouched] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false); // State for terms acceptance
  const [confirmPassword, setConfirmPassword] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const [showMyAccount, setShowMyAccount] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false); // Loading state

  useEffect(() => {
    const checkUserAuthentication = async () => {
      if (config.userData.accessToken) {
        setShowMyAccount(true);
      }
    };

    checkUserAuthentication();

  }, [config]);

  useEffect(() => {
    const detectCountry = () => {
      const browserLanguage = navigator.language || navigator.userLanguage;
      const [languageCode, countryCode] = browserLanguage.split('-');
      
      if (countryCode) {
        // If country code is available in the browser language
        setCountry(countryCode.toUpperCase());
      } else {
        // If not, try to find a country that uses the language
        const countries = getCountries();
        const matchingCountry = countries.find(country => country.toLowerCase() === languageCode.toLowerCase());
        setCountry(matchingCountry || 'US');
      }
    };
  
    detectCountry();
  }, []);

  useEffect(() => {
    console.log('Current country:', country);
  }, [country]);

  // Function to handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true); // Set loading to true when form submission starts
    try {
      // Check if terms are accepted
      if (!termsAccepted) {
        // Display error or prevent form submission
        return;
      }

      if (captchaToken === '' || captchaToken === null) {
        // Display error or prevent form submission
        setError('Please complete the reCAPTCHA verification');
        setIsLoading(false);
        return;
      }

      const response = await fetchData(`${config.backendUrl}/api/v2.0/user_registration`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...formData, recaptcha_token: captchaToken }),
      });

      // Handle response from the backend
      if (response.ok) {
        // Registration successful
        const result = await response.json();
        const accessToken = result.data[0]?.access_token;
        // Set config access token.
        config.setAccessToken(accessToken);
        // Fetch user data and update config.userData
        try {
          // Display success message
          setSuccessMessage('Registration successful! You will be redirected to your account page.');
          // Show the MyAccount component
          setShowMyAccount(true);
        } catch (error) {
          console.error('Error fetching user data:', error);
          // Display error message
          setError(`Registration successful, ${error.title}`);
          // Show the MyAccount component
          setShowMyAccount(true);
          // Get the redirect URL from the query parameters
          const urlParams = new URLSearchParams(window.location.search);
          const redirectUrl = urlParams.get('redirect');

          // Redirect to the specified URL or default to the home page
          if (redirectUrl) {
            window.location.href = redirectUrl;
          } else {
            window.location.href = '/';
          }
        }
      } else {
        // Registration failed
        const errorData = await response.json();
        const errorMessage = errorData.title || 'Registration failed. Please try again.';
        // Display error message
        setError(errorMessage);
      }
    } catch (error) {
      // Handle network error
      console.error('Network error:', error);
      // Display error message
      setError('Registration failed due to a network error. Please check your internet connection and try again.');
    } finally {
      setIsLoading(false); // Set loading to false when form submission ends
    }
  };

  // Function to handle input changes
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleConfirmPasswordChange = (e) => {
    setConfirmPassword(e.target.value);
    // Check if the field being changed is confirmPassword and update isConfirmPasswordTouched
    setIsConfirmPasswordTouched(true);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleMobileChange = (value) => {
    const formattedNumber = value ? formatPhoneNumberIntl(value) : '';
    setFormattedMobile(formattedNumber);
    setFormData({
      ...formData,
      mobile: value,
    });
  };

  if (config.success === false) {
    return (
      <Alert status="error" marginBottom="20px">
        <AlertIcon />
        {t('register.cannotAccessSettings')}
      </Alert>
    );
  }

  return (
    <>
      {showMyAccount ? (
        <MyAccount />
      ) : (
        <Box className="account">
           <h1>{t('register.title')}</h1>
          {successMessage && (
            <Alert status="success" marginBottom="20px">
              <AlertIcon />
              {t('register.successMessage')}
            </Alert>
          )}
          <Text marginBottom="30px">
            {t('register.instructions')}<br />
          </Text>
          <Text className="help-text" dangerouslySetInnerHTML={{ __html: t('register.requiredFields', { asterisk: '<span style="color: red;">*</span>' }) }} />

          <form onSubmit={handleSubmit}>
            <FormControl marginBottom="20px" isRequired>
              <FormLabel>{t('register.firstName')}</FormLabel>
               <InputGroup>
              <Input
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                placeholder={t('register.firstName')}
                required
                isDisabled={isLoading}
              />
                   </InputGroup>
            </FormControl>
            <FormControl marginBottom="20px" isRequired>
              <FormLabel>{t('register.lastName')}</FormLabel>
                           <InputGroup>
              <Input
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                placeholder={t('register.lastName')}
                required
                isDisabled={isLoading}
              />
                   </InputGroup>
            </FormControl>
            <FormControl marginBottom="20px" isRequired>
              <FormLabel>{t('register.email')}</FormLabel>
                           <InputGroup>
              <Input
                type="mail"
                name="mail"
                value={formData.mail}
                onChange={handleChange}
                placeholder={t('register.email')}
                required
                isDisabled={isLoading}
              />
                   </InputGroup>
            </FormControl>
            <FormControl marginBottom="20px" isRequired>
              <FormLabel className="phone-number-label">{t('register.mobileNumber')}</FormLabel>
              <PhoneInput
                className="phone-input-dropdown"
                country={country}
                value={formData.mobile}
                onChange={handleMobileChange}
                placeholder={t('register.enterLocalMobile')}
                onCountryChange={(countryCode) => setCountry(countryCode)}
                flags={flags}
                defaultCountry={country}
                required
                disabled={isLoading}
              />
            </FormControl>
            <Box>
              {formattedMobile && (
                <Text marginBottom="30px" fontSize="lg">
                  {t('register.standardInternationalFormat')}: {formattedMobile}
                </Text>
              )}
            </Box>

            <FormControl marginBottom="20px">
              <FormLabel>{t('register.newPassword')}</FormLabel>
              <InputGroup>
                <Input
                  type={showPassword ? 'text' : 'password'}
                  name="pass"
                  value={formData.pass}
                  onChange={handleChange}
                  placeholder={t('register.newPassword')}
                  isDisabled={isLoading}
                />
                <InputRightElement width="3rem">
                  <IconButton
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    variant="ghost"
                    onClick={togglePasswordVisibility}
                    icon={showPassword ? <MdVisibilityOff /> : <MdVisibility />}
                    isDisabled={isLoading}
                  />
                </InputRightElement>
              </InputGroup>
            </FormControl>
            <FormControl marginBottom="20px">
              <FormLabel>{t('register.confirmPassword')}</FormLabel>
              <InputGroup>
                <Input
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={confirmPassword}
                  onChange={handleConfirmPasswordChange}
                  placeholder={t('register.confirmPassword')}
                  onBlur={() => setIsConfirmPasswordTouched(true)}
                  isDisabled={isLoading}
                />
                <InputRightElement width="3rem">
                  <IconButton
                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                    variant="ghost"
                    onClick={toggleConfirmPasswordVisibility}
                    icon={showConfirmPassword ? <MdVisibilityOff /> : <MdVisibility />}
                    isDisabled={isLoading}
                  />
                </InputRightElement>
              </InputGroup>
              <FormControl marginBottom="5px">
                {isConfirmPasswordTouched && formData.pass !== confirmPassword && (
                  <Flex alignItems="center" ml="5px">
                    <MdError style={{ color: 'red' }} />
                    <span style={{ marginLeft: '5px', color: 'red' }}>{t('register.passwordsDoNotMatch')}</span>
                  </Flex>
                )}
                {isConfirmPasswordTouched && formData.pass === confirmPassword && confirmPassword !== '' && (
                  <Flex alignItems="center" ml="5px">
                    <MdCheckCircle style={{ color: 'green' }} />
                    <span style={{ marginLeft: '5px', color: 'green' }}>{t('register.passwordsMatch')}</span>
                  </Flex>
                )}
              </FormControl>
            </FormControl>

            <FormControl marginBottom="20px" isRequired className="terms-and-conditions">
              <Flex alignItems="center">
                <input
                  type="checkbox"
                  id="termsCheckbox"
                  checked={termsAccepted}
                  onChange={() => setTermsAccepted(!termsAccepted)}
                  style={{ marginRight: '10px' }}
                  required
                  disabled={isLoading}
                />
                <FormLabel htmlFor="termsCheckbox">
                  <Trans i18nKey="register.termsAcceptance">
                    I have read and accepted the Auctioneers <a href="/terms" target="_blank" rel="noopener noreferrer">Terms and Conditions</a> and <a href="/conditions" target="_blank" rel="noopener noreferrer">Data Privacy Terms</a> and I agree to abide by them.
                  </Trans>
                </FormLabel>
              </Flex>
            </FormControl>

            <Box marginBottom="30px">
              <ReCAPTCHA
                sitekey={config.recaptchaKey}
                onChange={(token) => setCaptchaToken(token)}
                theme="light"
                hl={i18n.language} // Pass the language code here
                disabled={isLoading}
              />
            </Box>

            <Button type="submit" colorScheme="blue" isDisabled={isLoading}>
              {isLoading ? <Spinner size="sm" /> : t('register.signUpButton')}
            </Button>
          </form>
          {error && (
            <Alert status="error" marginBottom="20px">
              <AlertIcon />
              <AlertTitle>{t('register.errorTitle')}</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </Box>
      )}
    </>
  );
};

export default Register;